/**
 * IncomeShapeEditor — the scalar Select+PATCH editor for the income_shape
 * row (SILL-537 M5, vf-shape-editable). Mirrors CreditRow's / DrawerValue
 * Editor's draft-vs-initial + useProfileEdit pattern for the single
 * PATCH-able `income_shape` field, and additionally re-reads
 * GET /api/verification/path after a successful save so the row's verify
 * explanation updates immediately — no funnel re-entry required (scenario
 * 1714).
 *
 * Deliberately does NOT read `path.link_mode` — this editor only renders
 * `path.reason` as explanatory copy. Initiating a Plaid Link session from
 * the server's link_mode is out of scope here (see the coder brief for
 * task 1643 / SILL-537 M5): `linkModeOrFallback` in useVerificationPath.ts
 * is the MFA-safety clamp for that follow-up, tracked as task 1642.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import type { DashboardSnapshot } from "../../../../lib/dashboard";
import {
  useProfileEdit,
  type UseProfileEditOptions,
} from "../../../../lib/profile/use-profile-edit";
import { getVerificationPath } from "../../../../lib/verification-api";
import { Select } from "../../../ui/select";
import { INCOME_SHAPE_ITEMS } from "./income-shape-row.helpers";

export function IncomeShapeEditor({
  snapshot,
  opts,
}: {
  snapshot: DashboardSnapshot;
  opts: UseProfileEditOptions;
}) {
  const editProfile = useProfileEdit(opts);

  // Empty string (never a fabricated shape) is the "not set" draft value —
  // matches CreditRow's `snapshot.creditBucket ?? ""` idiom exactly.
  const initial = snapshot.incomeShape ?? "";
  const [draftState, setDraftState] = useState({ base: initial, value: initial });
  const draft = draftState.base === initial ? draftState.value : initial;
  const setDraft = (value: string) => setDraftState({ base: initial, value });

  // The live verify-path reason (scenario 721/1714 copy) — fetched once on
  // mount and re-fetched after every successful save so the explanation
  // reflects the path the NEW shape resolves to, without a page reload or
  // funnel re-entry.
  const [reason, setReason] = useState<string | null>(null);
  const [reasonLoading, setReasonLoading] = useState(false);

  // Mount-only fetch of the current verify-path reason. Guarded by
  // hasFetchedRef (mirrors useVerificationPath.ts's requestedRef) so the
  // fetch fires exactly once even under StrictMode's double-invoke.
  const hasFetchedRef = useRef(false);
  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    let cancelled = false;
    setReasonLoading(true);
    void (async () => {
      try {
        const path = await getVerificationPath(opts.getToken);
        if (!cancelled) setReason(path?.reason ?? null);
      } catch {
        // Best-effort — leave whatever reason we already have rather than
        // blocking the editor.
      } finally {
        if (!cancelled) setReasonLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Intentionally mount-only — re-fetches are triggered explicitly by
    // handleChange after a successful save, not by a dependency change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch after a successful save. This runs from a promise callback in
  // handleChange (an event handler), never from inside a React effect, so
  // the react-hooks/set-state-in-effect rule that shaped the mount effect
  // above doesn't apply here.
  const refetchReason = useCallback(async () => {
    setReasonLoading(true);
    try {
      const path = await getVerificationPath(opts.getToken);
      setReason(path?.reason ?? null);
    } catch {
      // Best-effort — leave whatever reason we already have.
    } finally {
      setReasonLoading(false);
    }
  }, [opts.getToken]);

  const error = editProfile.error
    ? "Couldn't save — please try again."
    : undefined;

  const handleChange = (next: string) => {
    if (next === initial) {
      setDraft(next);
      return;
    }
    setDraft(next);
    editProfile
      .mutate({ income_shape: next })
      .then(() => {
        void refetchReason();
      })
      .catch(() => {
        // A rejected value (e.g. 422 invalid_income_shape) must not look
        // saved — revert the optimistic draft, same idiom as
        // DrawerValueEditor/CreditRow. The stale reason is left in place
        // deliberately: the path didn't change since the save failed.
        setDraft(initial);
      });
  };

  return (
    <Select
      description={
        !error && !reasonLoading && reason ? reason : undefined
      }
      disabled={editProfile.isPending}
      error={error}
      items={INCOME_SHAPE_ITEMS}
      label="How you earn"
      onValueChange={handleChange}
      placeholder="Select how you earn"
      value={draft}
    />
  );
}
